import React from 'react';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

// Chart geometry in SVG units; the SVG scales to the card width.
const W = 420;
const H = 220;
const PAD = { left: 36, right: 36, top: 44, bottom: 40 };
const baseline = H - PAD.bottom;

// An accelerating growth curve: no levels or exact figures, just the start and end bands.
const curve = [0, 0.07, 0.17, 0.32, 0.55, 1].map((rise, i, all) => ({
  x: PAD.left + (i * (W - PAD.left - PAD.right)) / (all.length - 1),
  y: PAD.top + 22 + (1 - rise) * (baseline - PAD.top - 40)
}));

// Catmull-Rom spline through the points, written as cubic Bézier segments.
const linePath = curve.reduce((d, p, i) => {
  if (i === 0) return `M ${p.x} ${p.y}`;
  const p0 = curve[i - 2] || curve[i - 1];
  const p1 = curve[i - 1];
  const p3 = curve[i + 1] || p;
  const c1 = [p1.x + (p.x - p0.x) / 6, p1.y + (p.y - p0.y) / 6];
  const c2 = [p.x - (p3.x - p1.x) / 6, p.y - (p3.y - p1.y) / 6];
  return `${d} C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${p.x} ${p.y}`;
}, '');
const first = curve[0];
const last = curve[curve.length - 1];
const areaPath = `${linePath} L ${last.x} ${baseline} L ${first.x} ${baseline} Z`;
const gridLines = [0, 1, 2, 3].map((i) => PAD.top + (i * (baseline - PAD.top)) / 3);

const skills = ['DSA', 'System Design', 'Full-Stack', 'Agentic AI'];

const HeroSalaryChart = () => (
  <div className="salary-card">
    <span className="salary-target">
      <TrackChangesIcon sx={{ fontSize: 16 }} />
      For product-based companies &amp; AI startups
    </span>

    <div className="salary-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Salary growing from under 15 LPA to over 45 LPA">
        <defs>
          <linearGradient id="salary-line" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="55%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
          <linearGradient id="salary-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridLines.map((y) => (
          <line key={y} className="salary-grid" x1={PAD.left - 20} x2={W - PAD.right + 20} y1={y} y2={y} />
        ))}

        <path className="salary-area" d={areaPath} fill="url(#salary-area)" />
        <path className="salary-line" d={linePath} stroke="url(#salary-line)" pathLength="1" />

        <g className="salary-point salary-point--start">
          <line className="salary-guide" x1={first.x} x2={first.x} y1={first.y} y2={baseline} />
          <circle className="salary-dot" cx={first.x} cy={first.y} r="5" />
          <text className="salary-value" x={first.x - 6} y={first.y - 16}>&lt; ₹15 LPA</text>
        </g>

        <g className="salary-point salary-point--end">
          <line className="salary-guide" x1={last.x} x2={last.x} y1={last.y} y2={baseline} />
          <circle className="salary-halo" cx={last.x} cy={last.y} r="13" />
          <circle className="salary-dot" cx={last.x} cy={last.y} r="6" />
          <text className="salary-value salary-value--end" x={last.x + 6} y={last.y - 18} textAnchor="end">&gt; ₹45 LPA</text>
        </g>

        <text className="salary-axis" x={first.x} y={H - 12} textAnchor="start">Today</text>
        <text className="salary-axis salary-axis--end" x={last.x} y={H - 12} textAnchor="end">With in-demand skills</text>
      </svg>
    </div>

    <div className="salary-skills">
      <span>Skills that drive the jump</span>
      <ul>
        {skills.map((s, i) => <li key={s} style={{ '--i': i }}>{s}</li>)}
      </ul>
    </div>
  </div>
);

export default HeroSalaryChart;
